import * as PIXI from 'pixi.js';
import { DebugGridView } from 'js/bubbleCore/view/DebugGridView';
import { DebugHexSpaceView } from 'js/bubbleCore/view/DebugHexSpaceView';
import { Bubble } from '../bubbles/types';
import { BubblesView } from 'js/bubbleCore/view/BubblesView';
import { BubbleGameBoard } from '../BubbleGameBoard';
import { ShooterPointerView } from './ShooterPointerView';
import { ShooterView } from './ShooterView';
import { getHexPixelSize } from 'js/bubbleCore/view/utils/getHexPixelSize';
import { IHexPixelSize } from 'js/bubbleCore/view/types';
import { BouncersView } from 'js/bubbleCore/view/BouncersView';
import { PointZonesView } from 'js/bubbleCore/view/PointZonesView';
import { DeathLineView } from 'js/bubbleCore/view/entities/DeathLineView';
import { InputCatcher } from 'js/bubbleCore/view/InputCatcher';
import { getWholeBoardPixelSize } from 'js/bubbleCore/view/utils/getWholeBoardPixelSize';
import { IRectangle } from 'framework/utils/math-utils';
import { ITestShootResult } from 'js/bubbleCore/handlers/testShoot';
import { connectSignalToHasEventHandlers } from 'js/bubbleCore/events';
import { Layout } from 'js/layouts/GameBoardLayout';
import { TweenManager } from 'framework/TweenManager/TweenManager';
import { BoardAnimationLayer } from 'js/bubbleCore/view/BoardAnimationLayer';
import { BoardSounds } from 'js/bubbleCore/view/BoardSounds';
import { SoundManager, MuteReason } from 'framework/SoundManager/SoundManager';
import { saveUserData } from "js/sdk/saveUserData";

interface IBubbleGameBoardViewConfig {
  gameBoard: BubbleGameBoard;
  hexSize: number;
  scale: number;
  verticalOffset: number;
  soundManager: SoundManager;
}

export class BubbleGameBoardView {
  public readonly animationLayer: BoardAnimationLayer;
  public readonly tweenManager: TweenManager = new TweenManager();
  public readonly hexPixelSize: IHexPixelSize;

  private readonly layout: Layout;

  private readonly inputCatcher: InputCatcher;

  private readonly config: IBubbleGameBoardViewConfig;
  private readonly gameBoard: BubbleGameBoard;

  private readonly debugGrid: DebugGridView<Bubble>;
  private readonly debugSpace: DebugHexSpaceView<Bubble>;

  private readonly shooterPointerView: ShooterPointerView;
  private readonly shooterView: ShooterView;
  private readonly bouncersView: BouncersView;
  private readonly pointZonesView: PointZonesView;
  private readonly bubblesView: BubblesView;
  private readonly deathLineView: DeathLineView;

  private readonly boardPixelSize: IRectangle;

  constructor(config: IBubbleGameBoardViewConfig) {
    this.layout = new Layout();
    this.config = config;
    this.gameBoard = config.gameBoard;
    this.hexPixelSize = getHexPixelSize(config.hexSize, this.gameBoard.getData().grid.type);
    this.boardPixelSize = getWholeBoardPixelSize(this.gameBoard.getData(), this.hexPixelSize)
    this.inputCatcher = this.addChild(new InputCatcher(this));

    const { popOutSpace, grid, shooter } = this.getData();
    const { 
      pointZonesContainer,
      bouncersContainer,
      deathLineContainer,
      shooterContainer,
      shooterPointerContainer,
    } = this.layout;

    this.animationLayer = new BoardAnimationLayer(this.layout, this.tweenManager);
    this.pointZonesView = pointZonesContainer.addChild(new PointZonesView(popOutSpace, this.hexPixelSize, this.tweenManager));
    this.bouncersView = bouncersContainer.addChild(new BouncersView(popOutSpace, this.hexPixelSize));
    this.deathLineView = deathLineContainer.addChild(new DeathLineView(this));
    this.bubblesView = new BubblesView(this);
    this.shooterPointerView = shooterPointerContainer.addChild(new ShooterPointerView(this.getHexPixelSize(), shooter));
    this.shooterView = shooterContainer.addChild(new ShooterView(shooter, this, this.tweenManager));

    connectSignalToHasEventHandlers(
      this.gameBoard.signals.onEventProcessed,
      [
        this.pointZonesView,
        this.shooterView,
        this.bubblesView,
        this.bouncersView,
        this.deathLineView,
        new BoardSounds(),
      ]
    );

    this.adjustLayout(config);
  }

  private adjustLayout({scale, verticalOffset}: IBubbleGameBoardViewConfig): void {
    const boardWidth = this.getBoardPixelSize().width;
    this.layout.boardBackground.width = boardWidth;
    this.layout.boardContainer.scale.set(scale);
    this.layout.boardContainer.pivot.x = boardWidth * 0.5;
    this.layout.boardContainer.pivot.y = this.getHexPixelSize().height * verticalOffset;
    this.layout.leftSpace.pivot.x = (boardWidth * scale * 0.5);
    this.layout.rightSpace.pivot.x = -(boardWidth * scale * 0.5);

    const sfx = this.config.soundManager;

    const refreshSfxButton = () => {
      this.layout.soundLabelOn.visible = sfx.getSoundEnabled();
      this.layout.soundLabelOff.visible = !sfx.getSoundEnabled();
    };

    const refreshMusicButton = () => {
      this.layout.musicLabelOn.visible = sfx.getMusicEnabled();
      this.layout.musicLabelOff.visible = !sfx.getMusicEnabled();
    };

    this.layout.soundButton.on('pointerdown', () => {
      sfx.play("button_click");
      sfx.setSoundEnabled(!sfx.getSoundEnabled(), MuteReason.UserAction);
      refreshSfxButton();
      try {
        saveUserData({soundEnabled: sfx.getSoundEnabled()}); 
      } catch (e) {
        console.warn(e);
      }
    });

    this.layout.musicButton.on('pointerdown', () => {
      sfx.play("button_click");
      sfx.setMusicEnabled(!sfx.getMusicEnabled(), MuteReason.UserAction);
      refreshMusicButton();
      try {
        saveUserData({musicEnabled: sfx.getMusicEnabled()}); 
      } catch (e) {
        console.warn(e);
      }
    });

    refreshSfxButton();
    refreshMusicButton();
  }

  public addChild<T extends PIXI.DisplayObject>(displayObject: T): T {
    return this.layout.boardContainer.addChild(displayObject);
  }

  public getRoot(): PIXI.Container {
    return this.layout.root;
  }

  public getBoardPixelSize(useScaleFactor: boolean = false): IRectangle {
    return this.boardPixelSize;
  }

  public getData() {
    return this.gameBoard.getData();
  }

  public getHexPixelSize(): IHexPixelSize {
    return this.hexPixelSize;
  }

  public getInputCatcher(): InputCatcher {
    return this.inputCatcher;
  }

  public getLayout(): Layout {
    return this.layout;
  }

  public displayTestShoot(result: ITestShootResult): void {
    this.shooterPointerView.updateView(result,);
  }

  public update(delta: number): void {
    this.tweenManager.update(delta);
  }
}